import { apiFetch, buildQuery } from "./client";
import type { Page } from "./admin";

/**
 * Sprint 1 contract: docs/api/sprint1-lokacije-katalog.md §3-§4; sprint 5c (type = code, name,
 * description, attributes inline): docs/api/sprint5c-tip-dela-samo-atributi.md §3; sprint 5f
 * (attribute codebook, a type picks from it): docs/api/sprint5f-sifarnik-atributa.md; sprint 5k
 * (unit codebook, an attribute picks from it): docs/api/sprint5k-sifarnik-jedinica.md.
 */

// ---------------------------------------------------------------------------
// Unit codebook ("Šifarnik jedinica mere")
// ---------------------------------------------------------------------------

/** One unit of measure: the symbol shown next to a value, an optional spelled-out name and a note. */
export type UnitView = {
  id: string;
  /** "kW", "o/min", "°C"; unique among active rows, letter case ignored. */
  symbol: string;
  /** "kilovat", "obrtaja u minuti". */
  name: string | null;
  note: string | null;
  /** Active attributes measured in this unit. */
  attributeCount: number;
  createdAt: string;
  updatedAt: string;
};

export type UnitRequest = { symbol: string; name?: string | null; note?: string | null };

export const unitsApi = {
  /** The whole codebook, sorted by symbol; readable by everyone signed in. */
  list: () => apiFetch<UnitView[]>("/units"),
  /** 201; 409 SYMBOL_TAKEN (letter case ignored). */
  create: (body: UnitRequest) => apiFetch<UnitView>("/units", { method: "POST", body }),
  /** 409 SYMBOL_TAKEN. A new symbol shows on every attribute at once. */
  update: (id: string, body: UnitRequest) => apiFetch<UnitView>(`/units/${id}`, { method: "PUT", body }),
  /** 409 IN_USE (`details.attributeCount`) while an attribute is still measured in it. */
  remove: (id: string) => apiFetch<void>(`/units/${id}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Attribute codebook ("Šifarnik atributa")
// ---------------------------------------------------------------------------

export const ATTRIBUTE_DATA_TYPES = ["TEXT", "NUMBER", "BOOLEAN", "DATE", "ENUM", "RANGE"] as const;
export type AttributeDataType = (typeof ATTRIBUTE_DATA_TYPES)[number];

/** Serbian labels; rendered via t("attributeDefs.dataType.<TYPE>", fallback). */
export const ATTRIBUTE_DATA_TYPE_LABEL: Record<AttributeDataType, string> = {
  TEXT: "Tekst",
  NUMBER: "Broj",
  BOOLEAN: "Da/Ne",
  DATE: "Datum",
  ENUM: "Lista vrednosti",
  RANGE: "Opseg",
};

/** Contract 5f §2: lowercase latin, digits and underscore, starts with a letter; 2–50 chars. */
export const ATTRIBUTE_KEY_RE = /^[a-z][a-z0-9_]{1,49}$/;

/**
 * One row of an ENUM attribute's list (sprint 5f §6): `value` is what is stored under the attribute
 * key; `description` says what it means and shows wherever the value is picked or displayed.
 */
export type EnumOption = { value: string; description: string | null };

/** One codebook attribute: what it is, defined once; part types pick it. */
export type AttributeView = {
  id: string;
  /** Stable key the stored values are addressed by; immutable. */
  key: string;
  label: string;
  dataType: AttributeDataType;
  /** Row of the unit codebook; null = no unit. */
  unitId: string | null;
  /** Symbol of `unitId`, read-only. */
  unit: string | null;
  /** ENUM only: the allowed values with their descriptions. */
  enumOptions: EnumOption[] | null;
  /** Free note about the attribute itself: how it is measured, where it is read off… */
  note: string | null;
  /** Active part types that record this attribute. */
  typeCount: number;
  createdAt: string;
  updatedAt: string;
};

export type AttributeRequest = {
  key: string;
  label: string;
  dataType: AttributeDataType;
  /** Row of the unit codebook; null = no unit. 400 VALIDATION (`details.unitId`) when unknown. */
  unitId?: string | null;
  /** ENUM only: at least one row; a repeated value is 400 VALIDATION (`details["enumOptions[i].value"]`). */
  enumOptions?: EnumOption[] | null;
  note?: string | null;
};

export const attributesApi = {
  /** The whole codebook, sorted by label; readable by everyone signed in. */
  list: () => apiFetch<AttributeView[]>("/attributes"),
  /** 201; 409 KEY_TAKEN; 400 VALIDATION (`details.key`, `details.enumOptions`, `details.unitId`). */
  create: (body: AttributeRequest) => apiFetch<AttributeView>("/attributes", { method: "POST", body }),
  /** `key` is immutable (400 IMMUTABLE). */
  update: (id: string, body: AttributeRequest) =>
    apiFetch<AttributeView>(`/attributes/${id}`, { method: "PUT", body }),
  /** 409 IN_USE (`details.typeCount`) while a part type still records it. */
  remove: (id: string) => apiFetch<void>(`/attributes/${id}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Attributes of a part type
// ---------------------------------------------------------------------------

/**
 * One attribute a part type — or an article type (sprint 5l) — records: the codebook attribute
 * flattened with what the owner adds to it (`required`, `sortOrder`). Exactly one of
 * `assetTypeId` / `articleTypeId` is set; forms and cards ignore both.
 */
export type AttributeDefView = {
  id: string;
  assetTypeId: string | null;
  articleTypeId: string | null;
  attributeId: string;
  key: string;
  label: string;
  dataType: AttributeDataType;
  unit: string | null;
  /** ENUM only: the allowed values with their descriptions. */
  enumOptions: EnumOption[] | null;
  /** The attribute's note (how it is measured, where it is read off…); shown behind an "i" next to the label. */
  note: string | null;
  required: boolean;
  sortOrder: number;
};

export type AttributeDefRequest = {
  assetTypeId: string;
  attributeId: string;
  required?: boolean;
  sortOrder?: number;
};

/** One pick sent inline with the type (sprint 5f §3); `attributeId` is the sync identity on PUT. */
export type AttributeDefInline = {
  attributeId: string;
  required?: boolean;
  sortOrder?: number;
};

export const attributeDefsApi = {
  /** Attributes of one asset type, sorted by sortOrder then key. */
  list: (assetTypeId: string) =>
    apiFetch<AttributeDefView[]>(`/attribute-defs${buildQuery({ assetTypeId })}`),
  /** 409 KEY_TAKEN when the type already records the attribute. */
  create: (body: AttributeDefRequest) =>
    apiFetch<AttributeDefView>("/attribute-defs", { method: "POST", body }),
  /** `attributeId` and `assetTypeId` are immutable (400 IMMUTABLE). */
  update: (id: string, body: AttributeDefRequest) =>
    apiFetch<AttributeDefView>(`/attribute-defs/${id}`, { method: "PUT", body }),
  remove: (id: string) => apiFetch<void>(`/attribute-defs/${id}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Asset types
// ---------------------------------------------------------------------------

/** A RANGE value (sprint 5f §7): two numbers, either may be missing but not both. */
export type RangeValue = { min: number | null; max: number | null };

export type AttributeValue = string | number | boolean | RangeValue | null;

export function isRangeValue(v: unknown): v is RangeValue {
  return typeof v === "object" && v !== null && ("min" in v || "max" in v);
}

/** A type is a group of parts: what goes on a position and which data it records. */
export type AssetTypeView = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  /** Number of active attribute definitions. */
  attributeCount: number;
  createdAt: string;
  updatedAt: string;
};

/** The type-level sastavnica (`asset_type_bom`) is gone since sprint 5l — a sastavnica lives on a position. */
export type AssetTypeDetail = AssetTypeView & {
  /** Attributes picked for this type (what every asset / position of this type records). */
  attributeDefs: AttributeDefView[];
};

export type AssetTypeRequest = {
  code: string;
  name: string;
  description?: string | null;
  /**
   * The codebook attributes this type records. POST: written with the type in one transaction.
   * PUT: when present, synced by `attributeId` (new → insert, existing → update, missing → soft
   * delete); when absent, untouched.
   */
  attributeDefs?: AttributeDefInline[];
};

export type AssetTypeListParams = {
  q?: string;
  page?: number;
  size?: number;
  sort?: string;
};

export const assetTypesApi = {
  list: (params: AssetTypeListParams = {}) =>
    apiFetch<Page<AssetTypeView>>(`/asset-types${buildQuery(params)}`),
  get: (id: string) => apiFetch<AssetTypeDetail>(`/asset-types/${id}`),
  /** 201; 409 CODE_TAKEN; 400 VALIDATION (`details.attributeDefs[i].attributeId` for an unknown / repeated attribute). */
  create: (body: AssetTypeRequest) =>
    apiFetch<AssetTypeDetail>("/asset-types", { method: "POST", body }),
  update: (id: string, body: AssetTypeRequest) =>
    apiFetch<AssetTypeDetail>(`/asset-types/${id}`, { method: "PUT", body }),
  /** `reason` is mandatory (2026-09-22). */
  remove: (id: string, reason: string) => apiFetch<void>(`/asset-types/${id}?reason=${encodeURIComponent(reason)}`, { method: "DELETE" }),
  /** Attribute definitions of the type (same rows as `/attribute-defs?assetTypeId=`). */
  attributeDefs: (id: string) =>
    apiFetch<AttributeDefView[]>(`/asset-types/${id}/attribute-defs`),
};
